import React, { memo, useCallback, useEffect, useState } from "react";
import Icon from "../../components/Icon";

import PanelTemplate from "../../templates/Panel";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import PageHeader from "../../components/PageHeader";
import FilterSimple from "../../components/FilterSimple";
import Input from "../../components/Input";
import InputSelect from "../../components/InputSelect";
import InputFile from "../../components/InputFile";
import api from "../../services/api";
import { ModalDelete } from "../../components/ModalDelete";
import { toast } from "react-toastify";
import { Loading } from "../../components/Loading";
import { createOptions } from "../../helpers/selects";
import InputTextarea from "../../components/InputTextarea";
import { InputSeoComponent } from "../../components/InputSeo";
import { renderError } from "../../helpers/errors";
import { splitArray } from "../../helpers/conversions";
import { convertToSlug } from "../../helpers/masks";
import { canSeeBrands, hasPermission } from "../../services/auth";

const statusOptions = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false }
];

const featuredOptions = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
];

const formatProductsCount = count => `${Number(count) === 1 ? '1 produto' : `${count ?? 0} produtos`}`;

// Selo só onde há informação: no caso normal (aparece no site e nas marcas) a
// categoria fica limpa.
const CategoryFlags = ({category}) => (
    <>
        {category.empty && <span className="category-flag empty">Sem produtos</span>}
        {!category.empty && category.brand_only && canSeeBrands() && <span className="category-flag brand-only">Só nas marcas</span>}
    </>
);

// Componentes de grade em escopo de módulo (não recriados a cada render do
// CategoryList) e memoizados: assim digitar nos modais NÃO desmonta/reconstrói a
// lista inteira de categorias — só re-renderiza quando `categories` muda.
const SubCategory = memo(({category, theme, edit, setCategoryToDelete, readOnly}) => {
    return (
        <li className={`subcategory ${theme}`}>
            <div className="category-desc">
                {category?.image_url?.small ? (
                    <img src={category.image_url.small} alt="" />
                ) : (
                    <span className="category-placeholder"><Icon name="tag" size={12} /></span>
                )}
                <span className="category-name">{category.name}</span>
                <CategoryFlags category={category} />
            </div>
            <em className="count">{formatProductsCount(category.products_count)}</em>
            {!readOnly && hasPermission('edit_category') &&
                <button className="action action-edit" title="Editar subcategoria" onClick={() => edit(category)}><Icon name="edit" size={16} /></button>
            }
            {!readOnly && hasPermission('delete_category') &&
                <button className="action action-delete" title="Excluir subcategoria" onClick={() => setCategoryToDelete(category.id)}><Icon name="delete-small" size={16} /></button>
            }
        </li>
    );
});

const Category = memo(({category, edit, setCategoryToDelete, readOnly}) => {
    const [active, setActive] = useState(false);
    const subcategories = Array.isArray(category.categories) ? category.categories : [];

    return (
        <li className={active ? 'active' : ''}>
            <div className="category">
                <button className="category-title" title={active ? 'Recolher categoria' : 'Expandir categoria'} onClick={() => setActive(prev => !prev)}>
                    {category?.image_url?.small ? (
                        <img src={category.image_url.small} alt="" />
                    ) : (
                        <span className="category-placeholder"><Icon name="tag" size={12} /></span>
                    )}
                    <span className="category-name">{category.name}</span>
                    <CategoryFlags category={category} />
                    <em className="count">{formatProductsCount(category.products_count)}</em>
                </button>
                {!readOnly && hasPermission('edit_category') &&
                    <button className="action action-edit" title="Editar categoria" onClick={() => edit(category)}><Icon name="edit" size={16} /></button>
                }
                {!readOnly && hasPermission('delete_category') &&
                    <button className="action action-delete" title="Excluir categoria" onClick={() => setCategoryToDelete(category.id)}><Icon name="delete-small" size={16} /></button>
                }
                <button className="action action-toggle" title={active ? 'Recolher' : 'Expandir'} onClick={() => setActive(prev => !prev)}><Icon name="arrow-gray" className="arrow" /></button>
            </div>
            {Array.isArray(category.categories) &&
                <ul>
                    {subcategories.length === 0 &&
                        <li className="subcategory empty">
                            <span>Nenhuma subcategoria</span>
                        </li>
                    }
                    {subcategories.map((subcategory, index) => (
                        <SubCategory key={index} category={subcategory} theme={index % 2 === 0 ? 'dark' : 'light'} edit={edit} setCategoryToDelete={setCategoryToDelete} readOnly={readOnly} />
                    ))}
                </ul>
            }
        </li>
    );
});

export const CategoryList = () => {
    const [filters, setFilters] = useState('');
    const [brands, setBrands] = useState([]);
    const [viewBrandId, setViewBrandId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [createCategory, setCreateCategory] = useState(false);
    const [editCategory, setEditCategory] = useState(false);

    const [categoryToDelete, setCategoryToDelete] = useState(null);

    const [categoryId, setCategoryId] = useState(null);
    const [categoryName, setCategoryName] = useState('');
    const [categorySlug, setCategorySlug] = useState('');
    const [categoryIconBase64, setCategoryIconBase64] = useState('');
    const [categoryIconName, setCategoryIconName] = useState('');
    const [categoryDescription, setCategoryDescription] = useState('');
    const [categoryMetaTitle, setCategoryMetaTitle] = useState('');
    const [categoryMetaDescription, setCategoryMetaDescription] = useState('');
    const [categoryCategoryId, setCategoryCategoryId] = useState(null);
    const [categoryStatus, setCategoryStatus] = useState(true);
    const [categoryFeatured, setCategoryFeatured] = useState(false);

    useEffect(() => {
        getBrands();
    }, []);

    useEffect(() => {
        getCategories();
    }, [filters, viewBrandId]);

    useEffect(() => {
        if(!createCategory) clean();
    }, [createCategory]);

    useEffect(() => {
        if(!editCategory) clean();
    }, [editCategory]);
    
    useEffect(() => {
        if(createCategory){
            setCategorySlug(convertToSlug(categoryName));
            setCategoryMetaTitle(categoryName);
        }
    }, [categoryName]);

    useEffect(() => {
        if(createCategory){
            setCategoryMetaDescription(categoryDescription);
        }
    }, [categoryDescription]);

    const getBrands = () => {
        if (!canSeeBrands()) return;

        api.get(`/brand?per_page=100`).then(res => {
            const resources = res.data.resources?.data ?? res.data.resources ?? [];
            setBrands(resources.map(brand => ({ label: `Site da marca ${brand.name}`, value: brand.id })));
        }).catch(() => {
            setBrands([]);
        });
    };

    const getCategories = () => {
        setCategories([]);
        setLoading(true);

        const brandFilter = viewBrandId ? `&brand_id=${viewBrandId}` : '';

        api.get(`/category?${filters}${brandFilter}`).then(res => {
            setCategories(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const deleteCategory = () => {
        setLoading(true);
        
        api.delete(`/category/${categoryToDelete}`).then(res => {
            setCategoryToDelete(null);
            getCategories();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const storeCategory = () => {
        setLoading(true);

        api.post(`/category`, {
            category_id: categoryCategoryId,
            slug: categorySlug,
            name: categoryName,
            description: categoryDescription,
            meta_title: categoryMetaTitle,
            meta_description: categoryMetaDescription,
            icon: categoryIconBase64,
            status: categoryStatus,
            featured: categoryFeatured
        }).then(res => {
            toast.success('Categoria cadastrada com sucesso');
            setCreateCategory(false);
            getCategories();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const updateCategory = () => {
        setLoading(true);

        api.put(`/category/${categoryId}`, {
            category_id: categoryCategoryId,
            slug: categorySlug,
            name: categoryName,
            description: categoryDescription,
            meta_title: categoryMetaTitle,
            meta_description: categoryMetaDescription,
            icon: categoryIconBase64,
            status: categoryStatus,
            featured: categoryFeatured
        }).then(res => {
            toast.success('Categoria atualizada com sucesso');
            setEditCategory(false);
            getCategories();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    // useCallback com deps vazias: setters do useState são estáveis, então esta
    // referência não muda entre renders → os itens memoizados da grade não
    // re-renderizam ao editar os modais.
    const edit = useCallback(category => {
        setCategoryStatus(category.status);
        setCategoryId(category.id);
        setCategoryFeatured(category.featured);
        setCategoryName(category.name);
        setCategorySlug(category.slug);
        setCategoryIconBase64('');
        setCategoryIconName(category.icon ?? '');
        setCategoryDescription(category.description);
        setCategoryMetaTitle(category.meta_title);
        setCategoryMetaDescription(category.meta_description);
        setCategoryCategoryId(category.category_id);
        setEditCategory(true);
    }, []);

    const clean = () => {
        setCategoryStatus(true);
        setCategoryId(null);
        setCategoryCategoryId(null);
        setCategorySlug('');
        setCategoryName('');
        setCategoryDescription('');
        setCategoryMetaTitle('');
        setCategoryMetaDescription('');
        setCategoryIconName('');
        setCategoryIconBase64('');
        setCategoryFeatured(false);
    }

    return (
        <>
            <ModalDelete 
                show={categoryToDelete !== null}
                action={deleteCategory}
                close={() => setCategoryToDelete(null)}
                loading={loading}
            />
            <Modal
                title={`Cadastrar categoria`}
                size={`big`}
                show={createCategory}
                close={() => setCreateCategory(false)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Cadastrar`}
                        svg={`save-white`}
                        action={storeCategory}
                        loading={loading} 
                    />
                }
            >
                <div className="section">
                    <div className="row">
                        <div className="col-6">
                            <InputSelect label={`Status`} value={categoryStatus} change={setCategoryStatus} options={statusOptions} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Destaque`} value={categoryFeatured} change={setCategoryFeatured} options={featuredOptions} />
                        </div>
                    </div>                        
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Nome *`} value={categoryName} change={setCategoryName} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Categoria pai *`} value={categoryCategoryId} change={setCategoryCategoryId} options={createOptions(categories, 'name', 'id', true)} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Slug *`} value={categorySlug} change={setCategorySlug} mask={`convertToSlug`} />
                        </div>
                        <div className="col-6">
                            <InputFile 
                                label={`Ícone`} 
                                base64={categoryIconBase64}
                                changeBase64={setCategoryIconBase64}
                                name={categoryIconName}
                                changeName={setCategoryIconName}
                                description={`Tamanho ideal: 20px por 20px`}
                            />
                        </div>
                    </div>
                    <InputTextarea label={`Descrição *`} rows={3} value={categoryDescription} change={setCategoryDescription} />
                </div>
                <div className="section">
                    <Input label={`Meta title *`} value={categoryMetaTitle} change={setCategoryMetaTitle} />
                    <InputSeoComponent fields={[
                        {
                            label: 'Meta description *',
                            value: categoryMetaDescription,
                            onChange: setCategoryMetaDescription,
                            maxLength: 160,
                        }
                    ]} />
                </div>
            </Modal>
            <Modal
                title={`Editar categoria`}
                size={`big`}
                show={editCategory}
                close={() => setEditCategory(false)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Salvar alterações`}
                        svg={`save-white`}
                        action={updateCategory}
                        loading={loading} 
                    />
                }
            >
                <div className="section">
                    <div className="row">
                        <div className="col-6">
                            <InputSelect label={`Status`} value={categoryStatus} change={setCategoryStatus} options={statusOptions} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Destaque`} value={categoryFeatured} change={setCategoryFeatured} options={featuredOptions} />
                        </div>
                    </div>  
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Nome *`} value={categoryName} change={setCategoryName} />
                        </div>
                        <div className="col-6">
                            <InputSelect label={`Categoria pai *`} value={categoryCategoryId} change={setCategoryCategoryId} options={createOptions(categories, 'name', 'id', true, categoryId)} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Slug *`} value={categorySlug} change={setCategorySlug} />
                        </div>
                        <div className="col-6">
                            <InputFile 
                                label={`Ícone`} 
                                base64={categoryIconBase64}
                                changeBase64={setCategoryIconBase64}
                                name={categoryIconName}
                                changeName={setCategoryIconName}
                                description={`Tamanho ideal: 40px por 40px`}
                            />
                        </div>
                    </div>
                    <InputTextarea label={`Descrição *`} rows={3} value={categoryDescription} change={setCategoryDescription} />
                </div>
                <div className="section">
                    <Input label={`Meta title *`} value={categoryMetaTitle} change={setCategoryMetaTitle} />
                    <InputSeoComponent fields={[
                        {
                            label: 'Meta description *',
                            value: categoryMetaDescription,
                            onChange: setCategoryMetaDescription,
                            maxLength: 160,
                        }
                    ]} />
                </div>
            </Modal>
            <PanelTemplate id="category-list">
                <PageHeader
                    left={
                        <>
                            <FilterSimple 
                                params={[
                                    {name: 'Nome', key: '[name]', type: 'text' }
                                ]}
                                filter={setFilters}
                            />
                            {brands.length > 0 &&
                                <div className="category-view-as">
                                    <InputSelect
                                        label={`Vendo como`}
                                        value={viewBrandId}
                                        change={setViewBrandId}
                                        options={[{ label: 'Site principal', value: null }, ...brands]}
                                    />
                                </div>
                            }
                        </>
                    }

                    right={
                        <>
                            {!viewBrandId && hasPermission('edit_category') &&
                                <Button type={`secondary`} size={`small`} text={`Categoria de fornecedores`} svg="tags" link={`/company/categories`} />
                            }
                            {!viewBrandId && hasPermission('create_category') &&
                                <Button type={`primary`} size={`small`} text={`Cadastrar categoria`} svg="plus-white" action={() => setCreateCategory(true)} />
                            }
                        </>
                    }
                />
                <Loading show={loading} />
                {viewBrandId &&
                    <p className="category-view-hint">
                        Estas são as categorias que aparecem no site desta marca. A lista sai dos produtos que a marca exibe — não há cadastro de categoria por marca.
                    </p>
                }
                {!loading && categories.length === 0 &&
                    <div className="categories-empty">
                        <Icon name="tag" size={40} />
                        <h3>{viewBrandId ? 'Nenhuma categoria no site desta marca' : 'Nenhuma categoria'}</h3>
                        <p>
                            {viewBrandId
                                ? 'Vincule produtos à marca para que as categorias deles apareçam no site dela.'
                                : 'Cadastre uma categoria para começar a organizar os produtos do catálogo.'}
                        </p>
                    </div>
                }
                <div className="row">
                    {[0, 1, 2].map(column => (
                        <div className="col-4" key={column}>
                            <div className="categories">
                                <ul>
                                    {!loading && splitArray(categories, 3, column).map((category, index) => (
                                        <Category
                                            key={index}
                                            category={category}
                                            edit={edit}
                                            setCategoryToDelete={setCategoryToDelete}
                                            readOnly={viewBrandId !== null}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            
            </PanelTemplate>
        </>
    );
}

export default CategoryList;
